/**
 * Layout math for fixed A4 landscape card export frames (zoom scale-to-fit).
 * Reusable for any card-export feature; not review-specific.
 */
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Ref,
} from 'vue'

/** A4 landscape page (mm). Portrait was 210×297. */
export const EXPORT_PAGE_WIDTH_MM = 297
export const EXPORT_PAGE_HEIGHT_MM = 210
/** Usable card width inside landscape page with 8mm horizontal padding per side. */
export const EXPORT_CONTENT_WIDTH_MM = EXPORT_PAGE_WIDTH_MM - 16

/** ~96dpi inner frame (landscape A4 minus padding). Used when iframe is not sized yet. */
export const EXPORT_FRAME_WIDTH_PX = 1070
/** Full printable area height before footer reservation. */
export const EXPORT_FRAME_HEIGHT_PX = 752
/** Chrome header/footer bands; subtract from body fit area when measuring. */
export const EXPORT_HEADER_RESERVED_PX = 36
export const EXPORT_FOOTER_RESERVED_PX = 40
/** Small margin so card bottoms are not clipped by overflow:hidden. */
export const EXPORT_FIT_HEIGHT_BUFFER_PX = 10
export const EXPORT_BODY_HEIGHT_PX =
  EXPORT_FRAME_HEIGHT_PX - EXPORT_HEADER_RESERVED_PX - EXPORT_FOOTER_RESERVED_PX

/** Default floor when content exceeds the body frame at max scale. */
export const EXPORT_MIN_SCALE = 0.55
/**
 * Strategy card hits this floor on text-heavy companies and was clipping a
 * small slice off the bottom. Lowered to give worst-case cards extra shrink
 * headroom so they fit one page. Interim measure until LaTeX templates land.
 */
export const EXPORT_MIN_SCALE_STRATEGY = 0.45
/** Scale up to fill landscape page when content is smaller than the frame. */
export const EXPORT_MAX_SCALE = 1.75

export type ExportScaleFitMode = 'contain' | 'fill-frame'

function mmToPx(mm: number): number {
  return Math.round(mm * 3.7795275591)
}

export function resolveExportFrameSize(frame: HTMLElement): {
  width: number
  height: number
} {
  const width = frame.clientWidth
  const height = frame.clientHeight
  const fitHeight = Math.max(height - EXPORT_FIT_HEIGHT_BUFFER_PX, 100)

  if (width >= 900 && height >= 400) {
    return { width, height: fitHeight }
  }
  return {
    width: width > 0 ? Math.max(width, EXPORT_FRAME_WIDTH_PX) : EXPORT_FRAME_WIDTH_PX,
    height:
      height > 0
        ? Math.max(fitHeight, EXPORT_BODY_HEIGHT_PX - EXPORT_FIT_HEIGHT_BUFFER_PX)
        : EXPORT_BODY_HEIGHT_PX - EXPORT_FIT_HEIGHT_BUFFER_PX,
  }
}

/** Ensure card roots span the export frame before measuring. */
export function applyExportCardLayout(content: HTMLElement) {
  content.style.width = '100%'
  content.style.maxWidth = '100%'
  const card = content.querySelector('.v-card') as HTMLElement | null
  if (card) {
    card.style.width = '100%'
    card.style.maxWidth = 'none'
  }
}

/** Measure card at full frame width (offsetHeight after layout). */
export function measureExportCardContent(content: HTMLElement): {
  width: number
  height: number
} {
  const card = content.querySelector('.v-card') as HTMLElement | null
  const measureEl = card ?? content
  return {
    width: Math.ceil(measureEl.offsetWidth || measureEl.scrollWidth),
    height: Math.ceil(measureEl.scrollHeight),
  }
}

export function calculateScaleToFit(options: {
  contentWidth: number
  contentHeight: number
  frameWidth: number
  frameHeight: number
  maxScale?: number
  minScale?: number
}): number {
  const {
    contentWidth,
    contentHeight,
    frameWidth,
    frameHeight,
    maxScale = EXPORT_MAX_SCALE,
    minScale = EXPORT_MIN_SCALE,
  } = options
  if (
    contentWidth <= 0 ||
    contentHeight <= 0 ||
    frameWidth <= 0 ||
    frameHeight <= 0
  ) {
    return 1
  }

  const heightScale = frameHeight / contentHeight
  const widthScale = frameWidth / contentWidth
  return Math.max(minScale, Math.min(maxScale, heightScale, widthScale))
}

export function useScaleToFit(
  frameRef: Ref<HTMLElement | null>,
  contentRef: Ref<HTMLElement | null>,
  options?: {
    maxScale?: number
    minScale?: number
    absoluteMinScale?: number
  },
) {
  const scale = ref(1)
  const naturalHeight = ref(0)
  const frameWidth = ref(0)
  const frameHeight = ref(0)
  let resizeObserver: ResizeObserver | null = null

  const minScale = options?.minScale ?? EXPORT_MIN_SCALE
  const maxScale = options?.maxScale ?? EXPORT_MAX_SCALE
  const absoluteMinScale = options?.absoluteMinScale ?? minScale
  let debounceId: ReturnType<typeof window.setTimeout> | null = null

  const recalculate = () => {
    const frame = frameRef.value
    const content = contentRef.value
    if (!frame || !content) {
      scale.value = 1
      naturalHeight.value = 0
      frameWidth.value = 0
      frameHeight.value = 0
      return
    }

    const frameSize = resolveExportFrameSize(frame)
    frameWidth.value = frameSize.width
    frameHeight.value = frameSize.height

    content.style.zoom = '1'
    applyExportCardLayout(content)
    void content.offsetHeight
    const { width, height } = measureExportCardContent(content)
    const contentWidth = Math.max(width, frameSize.width)

    let nextScale = calculateScaleToFit({
      contentWidth,
      contentHeight: height,
      frameWidth: frameSize.width,
      frameHeight: frameSize.height,
      maxScale,
      minScale,
    })

    content.style.zoom = String(nextScale)
    void content.offsetHeight
    let fittedHeight = Math.ceil(measureExportCardContent(content).height)

    for (let pass = 0; pass < 5 && fittedHeight > frameSize.height; pass += 1) {
      if (nextScale <= absoluteMinScale) {
        break
      }
      nextScale = Math.max(
        absoluteMinScale,
        nextScale * (frameSize.height / fittedHeight) * 0.99,
      )
      content.style.zoom = String(nextScale)
      void content.offsetHeight
      fittedHeight = Math.ceil(measureExportCardContent(content).height)
    }

    if (fittedHeight > frameSize.height && height > 0) {
      const fitScale = (frameSize.height / height) * 0.98
      if (fitScale < nextScale) {
        nextScale = Math.max(absoluteMinScale, fitScale)
        content.style.zoom = String(nextScale)
        void content.offsetHeight
        fittedHeight = Math.ceil(measureExportCardContent(content).height)
      }
    }

    if (
      Math.abs(nextScale - scale.value) < 0.002 &&
      naturalHeight.value === fittedHeight
    ) {
      return
    }
    scale.value = nextScale
    naturalHeight.value = fittedHeight
  }

  const scheduleRecalculate = () => {
    if (debounceId) {
      window.clearTimeout(debounceId)
    }
    debounceId = window.setTimeout(() => {
      debounceId = null
      recalculate()
    }, 16)
  }

  onMounted(() => {
    if (!window.ResizeObserver) {
      recalculate()
      return
    }
    resizeObserver = new ResizeObserver(() => scheduleRecalculate())
    if (frameRef.value) {
      resizeObserver.observe(frameRef.value)
    }
    if (contentRef.value) {
      resizeObserver.observe(contentRef.value)
    }
    window.addEventListener('resize', scheduleRecalculate)
    recalculate()
  })

  watch([frameRef, contentRef], () => {
    if (resizeObserver && frameRef.value) {
      resizeObserver.observe(frameRef.value)
    }
    if (resizeObserver && contentRef.value) {
      resizeObserver.observe(contentRef.value)
    }
    scheduleRecalculate()
  })

  onBeforeUnmount(() => {
    if (debounceId) {
      window.clearTimeout(debounceId)
    }
    window.removeEventListener('resize', scheduleRecalculate)
    resizeObserver?.disconnect()
  })

  const scaledWidth = computed(() => frameWidth.value)
  /** Layout height after zoom is applied (Chrome includes zoom in box metrics). */
  const scaledHeight = computed(() => naturalHeight.value)

  const overflowsFrame = computed(
    () => scaledHeight.value > frameHeight.value + 1,
  )

  const atMinScale = computed(
    () => scale.value <= absoluteMinScale + 0.001,
  )

  /** zoom keeps children at 100% width; transform+layoutWidth left empty margin in the box. */
  const contentStyle = computed(() => ({
    width: '100%',
    zoom: scale.value,
  }))

  return {
    scale,
    naturalHeight,
    scaledWidth,
    scaledHeight,
    overflowsFrame,
    atMinScale,
    contentStyle,
    recalculate,
  }
}

export { mmToPx }
